import { useEffect, useRef, useState, useCallback } from "react";
import { useLocation, useNavigate, useRouteLoaderData, useSearchParams } from "react-router-dom";
import { HeaderActionsSlot, HeaderSearchLeftSlot, HeaderSearchRightSlot, HeaderTitleSlot } from "@/components/dashboard/header-actions";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Search, Bell, UserPlus, Plus, UserCog, LogOut, Circle, Volume2, VolumeX, X } from "lucide-react";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Badge,
  Button,
  Separator,
  SidebarTrigger,
} from "@landnest/ui";
import { api } from "@/lib/api";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import type { AuthResponse } from "@/types/auth";
import { isSoundEnabled, setSoundEnabled } from "@/lib/notification-sound";
import type { PartnerNotificationStatsResponse } from "@/types/notifications";
import type { AdminNotificationStatsResponse } from "@/types/admin-notifications";
import type { ProfileResponse } from "@/types/profile";

function getPageTitle(pathname: string): string {
  const parts = pathname.split("/").filter(Boolean);

  const pathToTitle: Record<string, string> = {
    account: "Account",
    dashboard: "Dashboard",
    partners: "Partners",
    properties: "Properties",
    sales: "Sales",
    buyers: "Buyers",
    commissions: "Commissions",
    reports: "Reports",
    analytics: "Analytics",
    settings: "Settings",
    roles: "Roles",
    staff: "Staff",
    "document-builder": "Document Builder",
    documents: "Documents",
    messages: "Messages",
    profile: "Profile",
  };

  const toTitle = (segment: string) =>
    segment
      .split("-")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");

  if (parts.length >= 2) {
    const section = parts[1];
    return pathToTitle[section] || toTitle(section);
  }

  // Single-segment paths like /profile, /unauthorized, etc. Look the segment
  // up directly so /profile shows "Profile" rather than the legacy "Dashboard"
  // fallback.
  if (parts.length === 1) {
    const section = parts[0];
    return pathToTitle[section] || toTitle(section);
  }

  return "Dashboard";
}

interface SiteHeaderProps {
  children?: React.ReactNode;
  title?: React.ReactNode;
}

const searchPlaceholders: Record<string, string> = {
  sales: "Search sales...",
  buyers: "Search buyers...",
  properties: "Search properties...",
  commissions: "Search commissions...",
  referrals: "Search referrals...",
  notifications: "Search notifications...",
  inbox: "Search notifications...",
  users: "Search users...",
  partners: "Search partners...",
  staff: "Search staff...",
  roles: "Search roles...",
  invite: "Search invites...",
};

export function SiteHeader({ children, title }: SiteHeaderProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { pathname } = location;
  const defaultTitle = getPageTitle(pathname);
  const displayTitle = title || defaultTitle;
  const isPartner = pathname.startsWith("/partner");
  const isSubmitSale = pathname.includes("submit-sale") || pathname.includes("sales/submit");

  // Detect detail pages (URLs with ID-like segments)
  const pathParts = pathname.split("/").filter(Boolean);
  const knownRoutes = new Set([
    "dashboard", "users", "partners", "buyers", "properties", "sales",
    "commissions", "roles", "staff", "activity-log", "analytics", "reports",
    "messages", "file-management", "settings", "notifications", "api-keys",
    "document-templates", "document-builder", "home", "submit-sale",
    "referrals", "bank-info", "invite", "profile", "process", "create",
    "new", "import", "export-history", "intake",
  ]);
  const isDetailPage = pathParts.length >= 3 && !knownRoutes.has(pathParts[pathParts.length - 1]);

  const [searchParams, setSearchParams] = useSearchParams();
  const [localSearch, setLocalSearch] = useState(searchParams.get("search") || "");
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [soundOn, setSoundOn] = useState(() => isSoundEnabled());
  const toggleSound = useCallback(() => {
    setSoundOn((prev) => {
      setSoundEnabled(!prev);
      return !prev;
    });
  }, []);
  const searchValue = searchParams.get("search") || "";
  const section = pathname.split("/").filter(Boolean)[1] || "";

  // Sections whose own pages don't consume `?search=`. On those pages the
  // search bar is still shown (it's part of the global header) but typing
  // navigates to a default destination so the input is actually useful.
  const isAccountant = pathname.startsWith("/accountant");
  const sectionsWithoutSearch = new Set([
    "", "home", "profile", "bank-info",
    // Display-only pages that don't consume ?search= — pressing Enter
    // redirects to the buyers search instead of silently polluting the URL.
    "dashboard", "analytics", "reports", "settings", "messages",
    "api-keys", "activity-log", "document-templates", "file-management",
  ]);
  // Accountant portal pages are purely data-vis (dashboard/reports/analytics)
  // and none consume ?search=, so treat the whole portal as non-consuming to
  // prevent the debounce from silently polluting the URL.
  const sectionConsumesSearch = !isAccountant && !sectionsWithoutSearch.has(section);
  const searchRedirectTarget = pathname.startsWith("/admin")
    ? "/admin/buyers"
    : pathname.startsWith("/partner")
      ? "/partner/buyers"
      : null;

  // Sync local search with URL when URL changes externally (e.g. browser
  // back/forward, deep-link, programmatic navigate). BUG-019 / BUG-065:
  // we must NOT sync when the URL change came from our own debounced
  // setSearchParams below — that path produced the "missing letters /
  // deletes without consent" race: the user types "Adebi", the 400ms
  // debounce fires writing ?search=Adebi to the URL, the sync effect
  // then setLocalSearch(searchValue) which clobbers any character typed
  // between the debounce firing and the URL becoming "Adebi".
  // suppressNextSyncRef short-circuits the next sync after our own
  // write, leaving real external URL changes untouched.
  const suppressNextSyncRef = useRef(false);
  useEffect(() => {
    if (suppressNextSyncRef.current) {
      suppressNextSyncRef.current = false;
      return;
    }
    setLocalSearch(searchValue);
  }, [searchValue]);

  // Debounce search param updates only on pages that actually consume
  // `?search=` (e.g. /partner/sales, /partner/buyers). On pages that don't
  // consume it (e.g. /partner/home, /profile), we no longer auto-redirect
  // while the user is typing — that yanked the page to /partner/buyers on
  // every keystroke (BUG-002). Submitting the input (Enter) is what
  // navigates there now; see handleSearchSubmit below.
  useEffect(() => {
    if (!sectionConsumesSearch) return;
    const timer = setTimeout(() => {
      if (localSearch === searchValue) return;
      suppressNextSyncRef.current = true;
      setSearchParams((prev) => {
        if (localSearch) prev.set("search", localSearch);
        else prev.delete("search");
        return prev;
      }, { replace: true });
    }, 400);
    return () => clearTimeout(timer);
  }, [
    localSearch,
    searchValue,
    setSearchParams,
    sectionConsumesSearch,
  ]);

  const documentBuilderTab = searchParams.get("tab") === "recent" ? "recent" : "templates";
  const placeholder = (() => {
    if (section === "document-builder") {
      return documentBuilderTab === "recent"
        ? "Search generated docs..."
        : "Search templates...";
    }
    if (searchPlaceholders[section]) {
      return searchPlaceholders[section];
    }
    // Non-consuming sections (home, profile, bank-info). Make it explicit
    // that Enter is what submits, so typing no longer feels like it's
    // hijacking the page.
    if (!sectionConsumesSearch && searchRedirectTarget) {
      return pathname.startsWith("/admin")
        ? "Search buyers — press Enter"
        : "Search buyers — press Enter";
    }
    return "Search anything...";
  })();
  const isFocusedDocumentWorkspace =
    pathname.includes("/document-builder/create") ||
    pathname.includes("/document-builder/templates/") ||
    pathname.includes("/documents/create");

  const partnerAction = (() => {
    if (!isPartner) return null;
    if (pathname === "/partner/sales") {
      return { label: "Submit Sale", icon: Plus, href: "/partner/submit-sale" };
    }
    if (pathname === "/partner/buyers") {
      return { label: "Add Buyer", icon: UserPlus, href: "/partner/buyers/intake" };
    }
    if (pathname.startsWith("/partner/commissions")) {
      return null;
    }
    if (pathname === "/partner/referrals") {
      return { label: "Invite Partner", icon: UserPlus, href: "/partner/referrals?tab=invite" };
    }
    if (pathname === "/partner/inbox") {
      // Inbox provides its own contextual action (Mark all read) via HeaderActions portal.
      return null;
    }
    return { label: "Add Buyer", icon: UserPlus, href: "/partner/buyers/intake" };
  })();

  // Track previous section to clear stale search on page change. If the
  // user navigated explicitly to a URL that includes ?search=… (e.g. the
  // dashboard search redirect to /partner/buyers?search=…), respect it
  // instead of wiping it.
  const prevSectionRef = useRef(section);
  useEffect(() => {
    if (prevSectionRef.current !== section) {
      prevSectionRef.current = section;
      if (!searchValue) {
        setLocalSearch("");
      }
    }
  }, [section, searchValue]);

  // Close mobile search when navigating to a different page
  useEffect(() => {
    setMobileSearchOpen(false);
  }, [pathname]);

  function handleSearchChange(e: React.ChangeEvent<HTMLInputElement>) {
    setLocalSearch(e.target.value);
  }

  function handleSearchKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    // On pages that don't consume `?search=`, pressing Enter is the explicit
    // submit that navigates to the search destination. While typing we no
    // longer redirect — that was the BUG-002 surprise.
    if (e.key !== "Enter") return;
    if (sectionConsumesSearch) return;
    if (!searchRedirectTarget) return;
    const query = localSearch.trim();
    if (!query) return;
    e.preventDefault();
    navigate(`${searchRedirectTarget}?search=${encodeURIComponent(query)}`);
  }

  const rootData = useRouteLoaderData("root") as AuthResponse | undefined;
  const baseUser = rootData?.user;
  const { data: profileData } = useQuery<ProfileResponse>({
    queryKey: ["profile"],
    queryFn: async () => {
      const res = await api.get<ProfileResponse>("/profile");
      return res.data;
    },
    enabled: !!baseUser?.id,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
  const user = baseUser
    ? {
        ...baseUser,
        name: profileData?.account?.name ?? baseUser.name,
        image: profileData?.account?.image ?? baseUser.image,
      }
    : baseUser;
  const initials =
    user?.name
      ?.split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2) || "U";

  const isPartnerRole = user?.role === "PARTNER";
  const isAccountantRole = user?.role === "ACCOUNTANT";
  const userRoleKey = user?.role?.toLowerCase() || "guest";

  // Route the bell to the correct notifications page based purely on the
  // user's role, not the current path. Accountants visit /admin/sales and
  // /admin/commissions from their sidebar, so a path-based check would send
  // them to the admin notifications page instead of their own.
  const notificationsPath = isPartnerRole
    ? "/partner/notifications"
    : isAccountantRole
      ? "/accountant/notifications"
      : user?.role
        ? "/admin/notifications"
        : null;

  const { data: notifStats } = useQuery({
    queryKey: ["notification-stats", userRoleKey],
    enabled: !!user?.role,
    queryFn: async () => {
      try {
        if (isPartnerRole) {
          const response = await api.get<PartnerNotificationStatsResponse>(
            "/partner/notifications/stats"
          );
          return response.data;
        }

        if (isAccountantRole) {
          const response = await api.get<AdminNotificationStatsResponse>(
            "/accountant/notifications/stats"
          );
          return response.data;
        }

        const response = await api.get<AdminNotificationStatsResponse>(
          "/admin/notifications/stats"
        );
        return response.data;
      } catch {
        return { total: 0, unread: 0, important: 0 };
      }
    },
    refetchInterval: 30000,
    retry: false,
  });

  const unreadCount = notifStats?.unread ?? 0;

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch { /* ignore */ }
    navigate("/login");
  };

  const accountHref = user?.role === "PARTNER" ? "/partner/account" : "/profile";

  const showSearch = !isDetailPage && !isSubmitSale && !isFocusedDocumentWorkspace;

  return (
    <header className="flex h-(--header-height) shrink-0 items-center gap-2 border-b px-4 lg:px-6 transition-[width,height] ease-linear group-has-data-[collapsible=icon]/sidebar-wrapper:h-(--header-height)">
      {/* Mobile search expanded — takes over the header row on small screens */}
      {mobileSearchOpen && showSearch && (
        <div className="flex sm:hidden flex-1 items-center gap-2">
          <button
            type="button"
            onClick={() => setMobileSearchOpen(false)}
            className="shrink-0 rounded-sm p-1 text-muted-foreground hover:text-foreground transition-colors"
            aria-label="Close search"
          >
            <ArrowLeft className="size-4" />
          </button>
          <div className="relative flex h-9 flex-1 items-center rounded-full border border-border bg-background focus-within:border-primary/40 focus-within:ring-2 focus-within:ring-primary/20 transition">
            <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
            <input
              autoFocus
              type="text"
              placeholder={placeholder}
              value={localSearch}
              onChange={handleSearchChange}
              onKeyDown={(e) => {
                if (e.key === "Escape") { setMobileSearchOpen(false); return; }
                handleSearchKeyDown(e);
                if (e.key === "Enter") setMobileSearchOpen(false);
              }}
              className="h-full w-full bg-transparent pl-9 pr-9 text-sm outline-none placeholder:text-muted-foreground"
            />
            {localSearch && (
              <button
                type="button"
                onClick={() => setLocalSearch("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                aria-label="Clear search"
              >
                <X className="size-4" />
              </button>
            )}
          </div>
        </div>
      )}

      {/* Normal header content — hidden on mobile when mobile search overlay is open */}
      <div className={mobileSearchOpen && showSearch ? "hidden sm:flex flex-1 min-w-0 items-center gap-2" : "flex flex-1 min-w-0 items-center gap-2"}>
        {/* Left: sidebar toggle + title */}
        <div className="flex min-w-0 items-center gap-1 lg:gap-2 shrink-0">
          <SidebarTrigger className="-ml-1" />
          <Separator
            orientation="vertical"
            className="mx-2 data-[orientation=vertical]:h-4"
          />
          {isSubmitSale ? (
            <button
              type="button"
              onClick={() => navigate(isPartner ? "/partner/sales" : "/admin/sales")}
              className="flex min-w-0 items-center gap-1.5 text-base font-medium text-muted-foreground hover:text-foreground transition-colors"
            >
              <ArrowLeft className="size-4 shrink-0" />
              <span className="truncate">Sales</span>
            </button>
          ) : isDetailPage ? (
            <button
              onClick={() => navigate(`/${pathParts.slice(0, 2).join("/")}`)}
              className="flex min-w-0 items-center gap-1.5 text-base font-medium text-muted-foreground hover:text-foreground transition-colors"
            >
              <ArrowLeft className="size-4 shrink-0" />
              <span className="truncate">{displayTitle}</span>
            </button>
          ) : (
            <h1 className="truncate text-base font-medium">{displayTitle}</h1>
          )}
          {isDetailPage && (
            <>
              <Separator orientation="vertical" className="mx-2 data-[orientation=vertical]:h-4 shrink-0" />
              <div className="min-w-0 truncate">
                <HeaderTitleSlot />
              </div>
            </>
          )}
          {isSubmitSale && (
            <>
              <span className="text-muted-foreground/50 mx-1">/</span>
              <h1 className="truncate text-base font-semibold">Submit Sale</h1>
            </>
          )}
        </div>

        {/* Center: Search input — hidden on mobile (sm:flex), submit-sale, and detail pages.
            On mobile the search icon button in the right section expands inline search. */}
        {showSearch && (
          <div className="hidden sm:flex min-w-0 flex-1 justify-center px-4">
            <div className="relative flex h-9 w-full max-w-md items-stretch rounded-full border border-border bg-background focus-within:border-primary/40 focus-within:ring-2 focus-within:ring-primary/20 transition">
              <HeaderSearchLeftSlot />
              <div className="relative flex-1">
                <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
                <input
                  type="text"
                  placeholder={placeholder}
                  value={localSearch}
                  onChange={handleSearchChange}
                  onKeyDown={handleSearchKeyDown}
                  className="h-full w-full bg-transparent pl-9 pr-3 text-sm outline-none placeholder:text-muted-foreground"
                />
              </div>
              <HeaderSearchRightSlot />
            </div>
          </div>
        )}
        {/* Spacer on non-search pages keeps the right cluster at the far edge */}
        {!showSearch && <div className="flex-1" />}
        {/* Mobile spacer so right icons sit at the far edge on pages that normally show search */}
        {showSearch && <div className="flex-1 sm:hidden" />}

        {/* Right: action buttons + notifications + avatar */}
        <div className="flex items-center gap-1 lg:gap-2 shrink-0">
          <HeaderActionsSlot />
          {children}

          {/* Partner action buttons — contextual to current page/tab, hidden on submit-sale */}
          {partnerAction && !isSubmitSale && (
            <Button
              size="sm"
              variant="outline"
              iconLeft={<partnerAction.icon className="size-4" />}
              className="h-9 px-2 md:px-4 text-sm font-medium"
              onClick={() => navigate(partnerAction.href)}
            >
              <span className="hidden md:inline">{partnerAction.label}</span>
            </Button>
          )}

          {/* Mobile search button — only on pages with search, only on mobile */}
          {showSearch && (
            <Button
              variant="ghost"
              size="icon"
              className="sm:hidden h-8 w-8 text-muted-foreground hover:text-foreground"
              onClick={() => setMobileSearchOpen(true)}
              aria-label="Search"
            >
              <Search className="h-4 w-4" />
            </Button>
          )}

          {/* Sound toggle */}
          <Button
            variant="ghost"
            size="icon"
            className={soundOn
              ? "h-8 w-8 text-foreground"
              : "h-8 w-8 text-muted-foreground hover:text-foreground"}
            aria-label={soundOn ? "Mute notification sounds" : "Unmute notification sounds"}
            onClick={toggleSound}
            title={soundOn ? "Sound on — click to mute" : "Sound muted — click to unmute"}
          >
            {soundOn ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
          </Button>

          {/* Notifications bell */}
          <Button
            variant="ghost"
            size="icon"
            className="relative h-8 w-8 text-muted-foreground hover:text-foreground"
            aria-label="Open notifications"
            onClick={() => notificationsPath && navigate(notificationsPath)}
            disabled={!notificationsPath}
          >
            <Bell className="h-4 w-4" />
            {unreadCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-brand px-1 text-[10px] font-bold text-white shadow-sm ring-2 ring-background">
                {unreadCount > 99 ? "99+" : unreadCount}
              </span>
            )}
          </Button>

          <Popover>
            <PopoverTrigger asChild>
              <button className="rounded-full outline-none focus-visible:ring-2 focus-visible:ring-primary/40">
                <Avatar className="h-8 w-8 cursor-pointer">
                  <AvatarImage src={user?.image ?? undefined} alt={user?.name ?? "User"} />
                  <AvatarFallback className="bg-primary/10 text-primary text-xs font-medium">{initials}</AvatarFallback>
                </Avatar>
              </button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-56 p-1">
              <div className="flex items-center gap-2 p-2">
                <Avatar className="h-8 w-8">
                  <AvatarImage src={user?.image ?? undefined} alt={user?.name ?? "User"} />
                  <AvatarFallback className="bg-primary/10 text-primary text-xs font-medium">{initials}</AvatarFallback>
                </Avatar>
                <div className="min-w-0 flex-1 text-left leading-tight">
                  <p className="truncate text-sm font-medium">{user?.name ?? user?.email ?? "User"}</p>
                  <p className="truncate text-xs text-muted-foreground">{user?.email ?? user?.role}</p>
                </div>
              </div>
              <div className="my-1 h-px bg-border" />
              <button
                onClick={() => navigate(accountHref)}
                className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground transition-colors cursor-pointer"
              >
                <UserCog className="size-4 text-muted-foreground" />
                Account
              </button>
              <div className="my-1 h-px bg-border" />
              <button
                onClick={logout}
                className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground transition-colors cursor-pointer"
              >
                <LogOut className="size-4 text-muted-foreground" />
                Log out
              </button>
            </PopoverContent>
          </Popover>
        </div>
      </div>
    </header>
  );
}
